import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Lets a tabbed page put its tabs in the header.
 *
 * The header is rendered by the layout and the page by an `<Outlet />`, so
 * they are siblings with no way to talk — the page knows its tabs, the header
 * owns the bar they belong in. This carries them across.
 *
 * Why the header rather than the page body: the tabs are navigation, and
 * navigation belongs in the chrome. Below the title they read as part of the
 * content and push it down by the height of two rows.
 */
const SectionTabsContext = createContext({
    section: null,
    setSection: () => {},
    clearSection: () => {},
});

export function SectionTabsProvider({ children }) {
    const [section, setSectionState] = useState(null);

    // Stable identities: a page sets this from an effect, and a new function
    // every render would re-run that effect for ever.
    const setSection = useCallback((next) => setSectionState(next), []);
    const clearSection = useCallback(() => setSectionState(null), []);

    const value = useMemo(
        () => ({ section, setSection, clearSection }),
        [section, setSection, clearSection],
    );

    return <SectionTabsContext.Provider value={value}>{children}</SectionTabsContext.Provider>;
}

export function useSectionTabs() {
    return useContext(SectionTabsContext);
}

export default SectionTabsContext;
